import { useState } from 'react';
import type { AiEvent, AiTag, MessageSummary } from '@momomail/shared';
import { api, errorText } from '../lib/bridge';
import { locale, t, tMessage } from '../lib/i18n';
import { openAddToCalendar } from './Calendar';
import { Icon } from './Icon';
import { confirmDialog } from './Overlay';
import { toast } from './Toasts';

/**
 * The buttons the tagging AI fills in on a mail (server tagger.ts): "加入日历" with the event it found — one click, no form; already in
 * the calendar means no button — and unsubscribing from what it thinks the user won't read. On the mail's page and in the mail list.
 */

/** The "加入日历" button to show: there is an event, not in the calendar yet, not waved away. */
export const eventToAdd = (tag: AiTag | null | undefined): AiEvent | null => (tag?.event && !tag.event.added && !tag.dismissed?.includes('event') ? tag.event : null);
/** The AI suggests unsubscribing (and the user hasn't waved it away). */
export const suggestsUnsubscribe = (tag: AiTag | null | undefined): boolean => !!tag?.unsubscribe && !tag.dismissed?.includes('unsubscribe');

/** When an AI event is: "10月22日周四 14:00–15:00", "10月25日周日 全天". */
export function eventWhen(e: AiEvent): string {
  const day = (d: Date, zone?: string) => d.toLocaleDateString(locale(), { month: 'short', day: 'numeric', weekday: 'short', ...(zone ? { timeZone: zone } : {}) });
  const clock = (d: Date) => d.toLocaleTimeString(locale(), { hour: '2-digit', minute: '2-digit', hour12: false });
  if (e.allDay) {
    // (all-day dates are calendar days: read them as UTC so no zone shifts them)
    const start = new Date(`${e.start}T00:00:00Z`);
    const last = new Date(Date.parse(`${e.end}T00:00:00Z`) - 86_400_000);
    return `${last > start ? `${day(start, 'UTC')}–${day(last, 'UTC')}` : day(start, 'UTC')} ${t('全天')}`;
  }
  const start = new Date(e.start);
  const end = new Date(e.end);
  return `${day(start)} ${clock(start)}–${start.toDateString() === end.toDateString() ? clock(end) : `${day(end)} ${clock(end)}`}`;
}

/** "加入日历": the AI's event straight into the calendar; no calendar connected: the add dialog, filled in. The tag as it is now (null: unchanged). */
export async function addAiEvent(messageId: number, event: AiEvent): Promise<AiTag | null> {
  try {
    const r = await api().mail.addAiEvent(messageId);
    if (r.status === 'added') toast(t('已加入日历：{title}', { title: event.title }), 'ok');
    else if (r.status === 'already') toast(t('日历里已经有了'), 'info');
    else openAddToCalendar({ messageId, event: { ...event, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone } });
    return r.tag;
  } catch (e) {
    toast(errorText(e), 'error');
    return null;
  }
}

/**
 * Unsubscribe from a mail's sender. One-click is instant; a web page is worked by the AI on the server (can take a minute); sending an
 * unsubscribe email asks first; a page the AI can't finish opens in the browser. True when it is done.
 */
export async function runUnsubscribe(messageId: number, confirmMail = false): Promise<boolean> {
  try {
    const r = await api().mail.unsubscribe(messageId, confirmMail);
    if (r.status === 'done') {
      toast(tMessage(r.note) || t('已退订'), 'ok');
      return true;
    }
    if (r.status === 'confirm-mail') {
      const choice = await confirmDialog({
        title: t('发一封退订邮件？'),
        message: t('这个发件人要求用邮件退订：会用你的邮箱给 {to} 发一封主题为"{subject}"的邮件。', { to: r.to, subject: r.subject }),
        confirm: t('发送退订邮件'),
      });
      if (choice === 'confirm') return await runUnsubscribe(messageId, true);
    } else if (r.status === 'manual') {
      toast(tMessage(r.note), 'info');
      void api().mail.openExternal(r.url);
      // The user finishes on the page; then the button should go (from this sender's other mail too)
      const choice = await confirmDialog({ title: t('在网页上退订好了吗？'), message: t('退订页面已在浏览器里打开。在那里退订完以后点"已退订"，这个发件人的退订按钮就不再出现。'), confirm: t('已退订') });
      if (choice === 'confirm') {
        await api().mail.unsubscribeDone(messageId);
        toast(t('已退订'), 'ok');
        return true;
      }
    } else toast(tMessage(r.note), 'error');
  } catch (e) {
    toast(errorText(e), 'error');
  }
  return false;
}

/** On the mail's page: the event the AI found, with "加入日历" (and "不用了"); once added, "已加入日历" with "撤销". */
export function AiEventCard({ messageId, tag: initial }: { messageId: number; tag: AiTag }) {
  const [tag, setTag] = useState(initial);
  const [busy, setBusy] = useState(false);
  const e = tag.event;
  // Waved away, or found in the calendar already (it was there before this button): nothing to show
  if (!e || tag.dismissed?.includes('event') || e.added?.found) return null;
  const run = async (action: () => Promise<AiTag | null>) => {
    setBusy(true);
    const next = await action().catch((err: unknown) => (toast(errorText(err), 'error'), null));
    setBusy(false);
    if (next) setTag(next);
  };
  const added = !!e.added;
  return (
    <div className={`ai-event ${added ? 'ai-event--added' : ''}`}>
      <Icon name={added ? 'check' : 'calendarPlus'} size={16} />
      <div className="ai-event__text">
        <strong>{e.title}</strong>
        <span className="muted">
          {[eventWhen(e), e.location].filter(Boolean).join(' · ')}
          {added && ` · ${t('已加入日历')}`}
        </span>
      </div>
      {added ? (
        <button className="btn btn--small" disabled={busy} onClick={() => void run(() => api().mail.undoAiEvent(messageId))}>
          {busy ? <span className="spinner" /> : t('撤销')}
        </button>
      ) : (
        <>
          <button className="btn btn--icon btn--quiet" title={t('不用了')} aria-label={t('不用了')} disabled={busy} onClick={() => void run(() => api().mail.dismissAi(messageId, 'event'))}>
            <Icon name="x" size={14} />
          </button>
          <button className="btn btn--small btn--primary" disabled={busy} onClick={() => void run(() => addAiEvent(messageId, e))}>
            {busy ? <span className="spinner" /> : t('加入日历')}
          </button>
        </>
      )}
    </div>
  );
}

/** In a mail list row: the AI's buttons as small chips, so they work without opening the mail (clicks don't open it). */
export function AiRowButtons({ message }: { message: MessageSummary }) {
  const [busy, setBusy] = useState<'event' | 'unsubscribe' | null>(null);
  // Done here: gone at once (the list reloads soon after, without it)
  const [unsubscribed, setUnsubscribed] = useState(false);
  const event = eventToAdd(message.aiTag);
  const unsubscribe = suggestsUnsubscribe(message.aiTag) && !unsubscribed;
  if (!event && !unsubscribe) return null;
  const run = (which: 'event' | 'unsubscribe', action: () => Promise<unknown>) => (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy) return;
    setBusy(which);
    void action().finally(() => setBusy(null));
  };
  return (
    <div className="row__ai-buttons">
      {event && (
        <button className="ai-chip" disabled={!!busy} onClick={run('event', () => addAiEvent(message.id, event))}>
          {busy === 'event' ? <span className="spinner spinner--tiny" /> : <Icon name="calendarPlus" size={12} />} {t('加入日历')} · {eventWhen(event)}
        </button>
      )}
      {unsubscribe && (
        <button className="ai-chip" disabled={!!busy} onClick={run('unsubscribe', async () => setUnsubscribed(await runUnsubscribe(message.id)))}>
          {busy === 'unsubscribe' ? <span className="spinner spinner--tiny" /> : <Icon name="bellOff" size={12} />} {t('退订')}
        </button>
      )}
    </div>
  );
}
